import React, { Fragment, useState } from 'react';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { Button } from 'components/_new/Button';
import { Table } from 'components/_new/Table/Table';
import { LANGUAGE_FLAG_MAP } from 'components/_new/CountryFlag/CountryFlag';
import { useNavigate } from 'react-router-dom';
import { slugify } from 'utils/helpers';
import { RemoveArticleDialog } from 'components/programPanel/knowledgeBase/components/RemoveArticleDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'contexts/authContext';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { ArticlePresenterDialog } from './ArticlePresenterDialog';
import { ArticleItem } from './ArticleItem';

type ArticleListProps = {
	title?: string;
	items: KnowledgeBaseArticleContentItem[];
	disableRating?: boolean;
	variant?: 'card' | 'list';
};

export const ArticleList = ({ title, items, disableRating = false, variant }: ArticleListProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { knowledgeBaseLinkPrefix, context, isSearch } = useKnowledgeBase();

	const [selectedArticle, setSelectedArticle] = useState<number | null>(null);
	const [removeArticleDialogOpen, setRemoveArticleDialogOpen] = useState(false);

	const handleAfterRemoveArticle = () => {
		queryClient.removeQueries(['KNOWLEDGE_BASE_DIRECTORIES']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_SEARCH_LIST']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_TAG_LIST']);
		queryClient.removeQueries(['KNOWLEDGE_BASE_ARTICLE_DIRECTORY_LIST']);
	};

	if (context === 'user-panel') {
		const { currentUser } = useAuth();
		const isCardVariant = variant === 'card' || isSearch;
		const visibleItems = currentUser?.isProfessionalViewEnabled
			? items
			: items.filter((item) => !item.isProfessional);

		return (
			<>
				<ArticlePresenterDialog
					articleId={selectedArticle}
					visible={Boolean(selectedArticle)}
					onHide={() => {
						setSelectedArticle(null);
					}}
					disableRating={disableRating}
					knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
				/>
				<div
					className={classNames('flex flex-column gap-2', {
						'bg-gray-100 py-4 px-0 border-round-xl': isCardVariant,
					})}
				>
					{title && (
						<div>
							<div className="text-black font-bold text-base">{title}</div>
						</div>
					)}
					<div className="flex flex-column gap-6">
						{visibleItems.length === 0 ? (
							<span className="text-muted">{t('userPanel.files.fileNotFound')}</span>
						) : (
							<div className="md:flex-1">
								<ul
									className={classNames(
										'flex flex-column m-0 p-0',
										isCardVariant ? 'gap-2' : 'gap-2'
									)}
								>
									{visibleItems.map((data) => (
										<ArticleItem
											key={data.id}
											data={data}
											onPreviewClick={() => {
												setSelectedArticle(data.articleId);
											}}
											linkPrefix={knowledgeBaseLinkPrefix}
											bordered={isCardVariant}
											slim
										/>
									))}
								</ul>
							</div>
						)}
					</div>
				</div>
			</>
		);
	}

	if (['program-admin', 'coordinator'].includes(context)) {
		return (
			<>
				<RemoveArticleDialog
					articleId={selectedArticle}
					visible={removeArticleDialogOpen}
					onHide={() => {
						setRemoveArticleDialogOpen(false);
					}}
					onConfirm={handleAfterRemoveArticle}
				/>
				<Table<KnowledgeBaseArticleContentItem>
					data={items}
					columns={[
						{
							name: 'name',
							label: t('knowledgeBase.programPanel.article.title'),
							body: (item) => {
								const Flag = item.info.isFallback
									? LANGUAGE_FLAG_MAP[
											item.info.fallbackLanguageCode as keyof typeof LANGUAGE_FLAG_MAP
									  ]
									: Fragment;
								return (
									<div className="flex flex-row gap-2 align-items-center">
										{item.title}{' '}
										{item.info.isFallback ? <Flag style={{ width: '1.125rem' }} /> : null}
									</div>
								);
							},
							onCellClick: ({ articleId, slug, info: { fallbackLanguageCode } }) => {
								navigate({
									pathname: `${knowledgeBaseLinkPrefix}/article/${articleId}${
										slug ? `-${slugify(slug)}` : ''
									}`,
									search: isSearch ? `?lng=${fallbackLanguageCode}` : undefined,
								});
							},
						},
						{
							name: 'mentorAccess',
							label: t('knowledgeBase.programPanel.article.mentorAccess'),
							body: ({ info }) => (
								// eslint-disable-next-line react/jsx-no-useless-fragment
								<>
									{info?.access?.mentor ? (
										<span>
											<i className="mx-1 pi pi-check-circle text-green-400" />
										</span>
									) : (
										<span>
											<i className="mx-1 pi pi-times-circle text-red-400" />
										</span>
									)}
								</>
							),
							min: true,
						},
						{
							name: 'mentee',
							label: t('knowledgeBase.programPanel.article.menteeAccess'),
							body: ({ info }) => (
								// eslint-disable-next-line react/jsx-no-useless-fragment
								<>
									{info?.access?.mentee ? (
										<span>
											<i className="mx-1 pi pi-check-circle text-green-400" />
										</span>
									) : (
										<span>
											<i className="mx-1 pi pi-times-circle text-red-400" />
										</span>
									)}
								</>
							),
							min: true,
						},
						{
							name: 'visits',
							label: t('knowledgeBase.programPanel.article.views'),
							longLabel: t('knowledgeBase.programPanel.article.viewsTooltip'),
							body: ({ info }) => info.visits,
							min: true,
						},
						{
							name: 'action',
							label: '',
							min: true,
							bodyClassName: 'min200px',
							body: ({ articleId, info: { isSystem } }) => {
								if (isSystem) {
									return (
										<span className="text-muted text-sm">
											{t('knowledgeBase.programPanel.articleIsSystem')}
										</span>
									);
								}
								return (
									<div className="flex flex-row gap-1 flex-wrap">
										<span id={`edit-button-${articleId}`}>
											<Button
												label={t('actions.edit')}
												icon="pencil"
												onClick={() => {
													navigate({
														pathname: `${knowledgeBaseLinkPrefix}/edit/${articleId}`,
													});
												}}
												variant="primary-outlined"
												variantSize="sm"
											/>
										</span>
										<span id={`remove-button-${articleId}`}>
											<Button
												label={t('actions.remove')}
												icon="trash"
												onClick={() => {
													setSelectedArticle(articleId);
													setRemoveArticleDialogOpen(true);
												}}
												variant="danger-text"
												variantSize="sm"
											/>
										</span>
									</div>
								);
							},
						},
					]}
				/>
			</>
		);
	}

	return null;
};
